import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShoppingCart,
  FileText,
  PackageCheck,
  PackagePlus,
  Receipt,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import { Ban, XCircle, Trash2, Pencil } from "lucide-react";
import { canEditDraftPo } from "@/lib/draft-edit-eligibility";
import type { PurchaseOrderDetailDto, POLineItemDto } from "@procureflow/shared-types";
import { Attachments } from "@/components/attachments";
import { LineItemsTable } from "@/components/line-items-table";
import { PrintButton } from "@/components/print-button";
import type { ColumnDef } from "@/components/transaction-list-shell";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { SubmitForApprovalButton } from "@/components/submit-for-approval-button";
import { PO_STATUS_CONFIG } from "@/lib/status-config";
import { formatAmount, formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

// US-033: per-line receipt summary on PO detail. `received_qty_total` is the
// denormalized Good-condition cumulative — partial / full / over states are
// derived from it against the ordered quantity.
const RECEIPT_COLUMNS: ColumnDef<POLineItemDto>[] = [
  {
    id: "source_rfq",
    header: "Source RFQ",
    cellClassName: "font-mono text-xs text-muted-foreground",
    // US-064 — per-line provenance to the source RFQ line.
    cell: (li) => (li.source_rfq_id ? (li.source_rfq_number ?? "—") : "—"),
  },
  {
    id: "received",
    header: "Received",
    headerClassName: "text-right",
    cellClassName: "text-right tabular-nums font-medium",
    cell: (li) => {
      const r = li.received_qty_total;
      const over = r > li.quantity;
      const full = r === li.quantity;
      const partial = r > 0 && r < li.quantity;
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1",
            full && "[color:var(--success)]",
            (partial || over) && "[color:var(--warning)]",
            r === 0 && "text-muted-foreground",
          )}
        >
          {over && <AlertTriangle className="h-3.5 w-3.5" />}
          {r}
        </span>
      );
    },
  },
  {
    id: "outstanding",
    header: "Outstanding",
    headerClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (li) => {
      const diff = li.quantity - li.received_qty_total;
      if (diff > 0) return <span className="text-muted-foreground">{diff}</span>;
      if (diff === 0) return <span className="[color:var(--success)]">0</span>;
      return <span className="[color:var(--warning)]">+{-diff} over</span>;
    },
  },
];

export const Route = createFileRoute("/purchase-orders/$id")({
  head: () => ({
    meta: [{ title: "Purchase Order Detail — ProcureFlow" }],
  }),
  component: PODetailPage,
});

function PODetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PurchaseOrderDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  // US-029 cancel-action panel state.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // US-065 close-action panel state (cancel remaining receivable — distinct
  // from the US-029 cancel action which voids the whole PO).
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // #3 delete-draft action state — hard-delete a Draft PO, releasing its
  // source RFQ. A Draft PO has no GRN child, so deletion is always safe.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDetail() {
      // Detail pages do not get the MSW-ready guard for free (see brief §2).
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      try {
        const res = await fetchWithAuth(`/api/purchase-orders/${id}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setErrorStatus(res.status);
            setError(body.message ?? "Failed to load purchase order.");
          }
          return;
        }
        const { data } = await res.json();
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Link
          to="/purchase-orders"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Purchase Orders
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          {errorStatus === 403 ? (
            <>
              <p className="text-sm font-medium text-foreground">Access denied</p>
              <p className="text-sm text-muted-foreground">
                You do not have permission to view this purchase order.
              </p>
            </>
          ) : errorStatus === 404 ? (
            <>
              <p className="text-sm font-medium text-foreground">Not found</p>
              <p className="text-sm text-muted-foreground">
                This purchase order does not exist or has been removed.
              </p>
            </>
          ) : (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </div>
    );
  }

  const lineCount = detail.line_items.length;
  // US-030: receipts can be recorded against Sent / PartiallyReceived /
  // Received POs only, and recording is a ProcurementOfficer / Admin action.
  const canRecordReceipt =
    (user?.role === "ProcurementOfficer" || user?.role === "Admin") &&
    (detail.status === "Sent" ||
      detail.status === "PartiallyReceived" ||
      detail.status === "Received");
  // US-034: Record Invoice is gated on the same PO statuses but is a
  // Finance / ProcurementOfficer / Admin action — Finance captures the
  // bulk of recorded invoices in normal operation.
  const canRecordInvoice =
    (user?.role === "Finance" || user?.role === "ProcurementOfficer" || user?.role === "Admin") &&
    (detail.status === "Sent" ||
      detail.status === "PartiallyReceived" ||
      detail.status === "Received");
  // US-029: cancel-button gate. Mirrors the backend's first-passable
  // gates (role + status ∈ Draft|Sent + 0 GRNs + 0 invoices). The
  // INVOICE_BLOCKING + GRN_EXISTS responses remain authoritative on
  // the server; the FE gate just hides the button when those gates
  // would obviously fire so the user is not invited into a 400.
  const canCancel =
    (user?.role === "ProcurementOfficer" || user?.role === "Admin") &&
    (detail.status === "Draft" || detail.status === "Sent") &&
    detail.grns.length === 0 &&
    detail.invoices.length === 0;

  // US-057: a Draft PO is submitted into its approval chain.
  const canSubmitForApproval =
    (user?.role === "ProcurementOfficer" || user?.role === "Admin") && detail.status === "Draft";

  // US-065: a receivable PO (Sent / PartiallyReceived) can be closed —
  // cancelling its remaining receivable quantity without a further GR.
  const canClose =
    (user?.role === "ProcurementOfficer" || user?.role === "Admin") &&
    (detail.status === "Sent" || detail.status === "PartiallyReceived");

  // #3: a Draft PO can be deleted, releasing the demand it reserved on its
  // source RFQ. ProcurementOfficer / Admin only; Draft is the only deletable
  // state (a Draft PO has no downstream GRN child).
  const canDelete =
    (user?.role === "ProcurementOfficer" || user?.role === "Admin") && detail.status === "Draft";
  // US-077: a draft (Draft) PO can be fully edited (header + lines, re-syncing the RFQ).
  const canEdit = canEditDraftPo(detail.status, user?.role);

  async function handleCancel() {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const res = await fetchWithAuth(`/api/purchase-orders/${detail!.id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setCancelError(body.message ?? "Cancellation failed. Please try again.");
        return;
      }
      const { data } = await res.json();
      setDetail(data);
      setCancelOpen(false);
      setCancelReason("");
    } finally {
      setCancelLoading(false);
    }
  }

  // US-065: close a PO by cancelling its remaining receivable quantity without a
  // further GR. POSTs /api/purchase-orders/:id/close; the PO settles to Received.
  async function handleClose() {
    setCloseLoading(true);
    setCloseError(null);
    try {
      const res = await fetchWithAuth(`/api/purchase-orders/${detail!.id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(closeReason.trim() ? { reason: closeReason.trim() } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setCloseError(body.message ?? "Failed to close the purchase order.");
        return;
      }
      const { data } = await res.json();
      setDetail(data);
      setCloseOpen(false);
      setCloseReason("");
    } finally {
      setCloseLoading(false);
    }
  }

  // #3: delete a Draft PO. DELETEs /api/purchase-orders/:id; on 204 the PO is
  // gone and its source RFQ has been released, so we toast + navigate back to
  // the list (there is no detail to refresh).
  async function handleDelete() {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await fetchWithAuth(`/api/purchase-orders/${detail!.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setDeleteError(body.message ?? "Failed to delete the purchase order.");
        return;
      }
      setDeleteOpen(false);
      toast.success("Purchase order deleted — its source RFQ has been released.");
      navigate({ to: "/purchase-orders" });
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      setDeleteError("An unexpected error occurred.");
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to="/purchase-orders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Purchase Orders
      </Link>

      {/* Header card */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
                  <ShoppingCart className="h-3.5 w-3.5" />
                </div>
                <span className="font-mono text-sm font-medium text-muted-foreground">
                  {detail.po_number}
                </span>
              </div>
              <TransactionStatusBadge config={PO_STATUS_CONFIG[detail.status]} />
            </div>
            <h1 className="text-lg font-semibold tracking-tight leading-snug">
              {detail.vendor.company_name}
            </h1>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              <span>
                <span className="text-foreground font-medium">Contact:</span>{" "}
                {detail.vendor.contact_name}
                {detail.vendor.contact_email ? ` · ${detail.vendor.contact_email}` : ""}
              </span>
              <span>
                <span className="text-foreground font-medium">Payment Terms:</span>{" "}
                {detail.payment_terms ?? "—"}
              </span>
              <span>
                <span className="text-foreground font-medium">Delivery Terms:</span>{" "}
                {detail.delivery_terms ?? "—"}
              </span>
              <span>
                <span className="text-foreground font-medium">Created:</span>{" "}
                {formatDateTime(detail.created_at)}
              </span>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-bold tabular-nums tracking-tight">
              {formatAmount(detail.total_amount)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {lineCount} line{lineCount !== 1 ? "s" : ""}
            </div>
            <div className="mt-3 inline-flex items-center gap-2">
              <PrintButton aria-label="Export PO as PDF" />
            </div>
            {canRecordReceipt && (
              <Link
                to="/grn/receive/$poId"
                params={{ poId: detail.id }}
                className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <PackagePlus className="h-3.5 w-3.5" /> Record Receipt
              </Link>
            )}
            {canRecordInvoice && (
              <Link
                to="/invoices/new"
                search={{ po_id: detail.id }}
                className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
              >
                <Receipt className="h-3.5 w-3.5" /> Record Invoice
              </Link>
            )}
            {canSubmitForApproval && (
              <span className="mt-3 ml-2 inline-block align-top">
                <SubmitForApprovalButton
                  endpoint={`/api/purchase-orders/${detail.id}/submit`}
                  onSubmitted={(data) => setDetail(data as PurchaseOrderDetailDto)}
                />
              </span>
            )}
            {canCancel && !cancelOpen && (
              <button
                type="button"
                onClick={() => {
                  setCancelOpen(true);
                  setCancelReason("");
                  setCancelError(null);
                }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
              >
                <Ban className="h-3.5 w-3.5" /> Cancel PO
              </button>
            )}
            {canClose && !closeOpen && (
              <button
                type="button"
                data-testid="close-po-button"
                onClick={() => {
                  setCloseOpen(true);
                  setCloseReason("");
                  setCloseError(null);
                }}
                className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
              >
                <XCircle className="h-3.5 w-3.5" /> Close PO
              </button>
            )}
            {canEdit && (
              <Link
                to="/purchase-orders/build"
                search={{ editId: detail.id }}
                data-testid="edit-po-button"
                className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            )}
            {canDelete && !deleteOpen && (
              <button
                type="button"
                data-testid="delete-po-button"
                onClick={() => {
                  setDeleteOpen(true);
                  setDeleteError(null);
                }}
                className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
        </div>

        {/* #3 delete panel — hard-delete a Draft PO, releasing its source RFQ. */}
        {canDelete && deleteOpen && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 space-y-2">
            <p className="text-xs font-medium text-destructive">Delete this draft purchase order</p>
            <p className="text-xs text-muted-foreground">
              This permanently deletes the purchase order and releases the demand it reserved on its
              source RFQ, making that RFQ available to order again. This can't be undone.
            </p>
            {deleteError && <p className="text-xs text-destructive">{deleteError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="confirm-delete-po"
                onClick={handleDelete}
                disabled={deleteLoading}
                className="rounded-lg bg-destructive px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50 transition"
              >
                {deleteLoading ? "Deleting…" : "Delete PO"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeleteError(null);
                }}
                disabled={deleteLoading}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
              >
                Keep PO
              </button>
            </div>
          </div>
        )}

        {/* US-065 close panel — cancel remaining receivable without a further GR. */}
        {canClose && closeOpen && (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 space-y-2">
            <p className="text-xs font-medium text-foreground">Close this purchase order</p>
            <p className="text-xs text-muted-foreground">
              This cancels all remaining (un-received) quantity on this PO without a further goods
              receipt. Any quantity already received is unaffected. The PO will settle to Received.
            </p>
            <textarea
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
            />
            {closeError && <p className="text-xs text-destructive">{closeError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="confirm-close-po-button"
                onClick={handleClose}
                disabled={closeLoading}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition"
              >
                {closeLoading ? "Closing…" : "Confirm close"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCloseOpen(false);
                  setCloseReason("");
                  setCloseError(null);
                }}
                disabled={closeLoading}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
              >
                Keep PO open
              </button>
            </div>
          </div>
        )}

        {/* US-029 cancel reason panel — shown only while the cancel action is open. */}
        {canCancel && cancelOpen && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 space-y-2">
            <p className="text-xs font-medium text-destructive">Cancel this purchase order</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Cancellation reason (required)"
              rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-destructive/40 resize-none"
            />
            {cancelError && <p className="text-xs text-destructive">{cancelError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelLoading || !cancelReason.trim()}
                className="rounded-lg bg-destructive px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50 transition"
              >
                {cancelLoading ? "Cancelling…" : "Confirm cancellation"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCancelOpen(false);
                  setCancelReason("");
                  setCancelError(null);
                }}
                disabled={cancelLoading}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent transition"
              >
                Keep PO
              </button>
            </div>
          </div>
        )}

        {/* Cancellation reason — only for a cancelled PO (cancellation action is US-029) */}
        {detail.status === "Cancelled" && detail.cancellation_reason && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3">
            <p className="text-xs font-medium text-destructive mb-1">Cancellation Reason</p>
            <p className="text-sm text-foreground leading-relaxed">{detail.cancellation_reason}</p>
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Line Items</h2>
        </div>
        <LineItemsTable
          items={detail.line_items}
          totalAmount={detail.total_amount}
          extraColumns={RECEIPT_COLUMNS}
        />
      </div>

      {/* Linked documents. GRN rows link to GRN detail; invoice rows stay
          non-navigating until the invoice detail route lands. */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Linked Documents</h2>
        </div>
        <div className="p-5 space-y-5">
          {/* Source PR — only for the legacy single-source (generate-PO) path; a
              multi-RFQ PO (US-064) has no single source PR. */}
          {detail.source_pr && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Source Purchase Request
              </p>
              <Link
                to="/purchase-requests/$id"
                params={{ id: detail.source_pr.id }}
                className="inline-flex items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2 hover:bg-muted/40 transition-colors"
              >
                <div className="h-7 w-7 rounded-md bg-primary/10 grid place-items-center text-primary shrink-0">
                  <FileText className="h-3.5 w-3.5" />
                </div>
                <span className="font-mono text-xs font-medium">{detail.source_pr.pr_number}</span>
                <span className="text-sm text-muted-foreground truncate max-w-[280px]">
                  {detail.source_pr.title}
                </span>
              </Link>
            </div>
          )}

          {/* Source RFQ(s). US-064 — a PO built from multiple RFQs lists each
              distinct source RFQ from source_documents; the legacy award→PO path
              carries a single source_rfq. */}
          {detail.source_documents.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Source RFQs</p>
              <ul className="space-y-1.5">
                {detail.source_documents.map((doc) => (
                  <li key={doc.id}>
                    <Link
                      to="/rfq/$id"
                      params={{ id: doc.id }}
                      className="inline-flex items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs font-medium hover:bg-muted/40 transition-colors"
                    >
                      {doc.number}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : detail.source_rfq ? (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Source RFQ</p>
              <Link
                to="/rfq/$id"
                params={{ id: detail.source_rfq.id }}
                className="inline-flex items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs font-medium hover:bg-muted/40 transition-colors"
              >
                {detail.source_rfq.rfq_number}
              </Link>
            </div>
          ) : null}

          {/* Goods receipts */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Goods Receipts</p>
            {detail.grns.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No goods receipts recorded against this purchase order.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {detail.grns.map((grn) => (
                  <li key={grn.id}>
                    <Link
                      to="/grn/$id"
                      params={{ id: grn.id }}
                      className="flex items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2 hover:bg-muted/40 transition-colors"
                    >
                      <div className="h-7 w-7 rounded-md bg-[var(--success)]/10 grid place-items-center [color:var(--success)] shrink-0">
                        <PackageCheck className="h-3.5 w-3.5" />
                      </div>
                      <span className="font-mono text-xs font-medium">{grn.grn_number}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(grn.date_received)}
                      </span>
                      {grn.has_over_receipt && (
                        <span className="text-xs [color:var(--warning)]">over-receipt</span>
                      )}
                      {grn.has_damage && (
                        <span className="text-xs text-destructive">damage reported</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Invoices */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Invoices</p>
            {detail.invoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No invoices recorded against this purchase order.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {detail.invoices.map((inv) => (
                  <li
                    key={inv.id}
                    className="flex items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2"
                  >
                    <div className="h-7 w-7 rounded-md bg-primary/10 grid place-items-center text-primary shrink-0">
                      <Receipt className="h-3.5 w-3.5" />
                    </div>
                    <span className="font-mono text-xs font-medium">{inv.invoice_number}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(inv.invoice_date)}
                    </span>
                    <span className="text-xs text-muted-foreground">{inv.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <Attachments ownerType="purchase-orders" ownerId={id} />
    </div>
  );
}
